import { BookOpenText, Power, Settings } from "lucide-react"

import LanguageBox from "@/components/language-box"
import ThemeSelector from "@/components/theme-selector"
import ThemeSelectorLegacy from "@/components/theme-selector-legacy"
import { GamificationLeaderboard } from "@/components/gamification/GamificationHeader"
import { CompetitionRulesDialog } from "@/components/competition-rules-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/contexts/AuthContext"
import { useTranslation } from "react-i18next"

type HeaderControlsProps = {
  className?: string
  // 非学生（教师等）端：还原 github/dev 顶栏控制行 —— 規則 + 排行榜 + 主题切换(sage/ocean/taro)
  // + 语言(繁/EN) + 文本登出，全部沿用 github/dev 旧版样式（绿色按钮）。主题切换走旧版
  // ThemeSelectorLegacy（写 body 的 data-theme，配合 global.css 的 [data-skin=legacy][data-theme=…]）。
  legacy?: boolean
}

function RulesButton({ compact = false, legacy = false }: { compact?: boolean; legacy?: boolean }) {
  const { t } = useTranslation()
  const trigger = legacy ? (
    // 非学生（教师等）端：还原 github/dev 旧版規則按钮（绿色 bg-primary，文字 ≥1180px 才显示）
    <Button
      type="button"
      variant="outline"
      title={t("common.rules", "規則")}
      className="relative overflow-hidden group rounded-xl bg-primary border-2 border-background px-3 py-2 text-xs font-bold text-white transition-all hover:border-primary hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px] hover:shadow-primary/60 hover:bg-primary/10 hover:text-primary active:translate-y-0 active:shadow-none"
    >
      <BookOpenText className="h-4 w-4" />
      <span className="font-bold font-yusei-magic hidden min-[1180px]:inline">{t("common.rules", "規則")}</span>
    </Button>
  ) : (
    <button
      type="button"
      title={t("common.rules", "規則")}
      className={
        compact
          ? "inline-flex items-center justify-center rounded-full bg-accent [html[data-theme=orange]_&]:bg-[#8FCEE0] px-3 py-1.5 text-xs font-medium text-[#1F2937] hover:brightness-95 transition"
          : "inline-flex items-center justify-center rounded-full bg-accent [html[data-theme=orange]_&]:bg-[#8FCEE0] px-4 py-2 text-sm font-medium text-[#1F2937] hover:brightness-95 transition"
      }
    >
      {t("common.rules", "規則")}
    </button>
  )
  return <CompetitionRulesDialog>{trigger}</CompetitionRulesDialog>
}

export default function HeaderControls({ className, legacy = false }: HeaderControlsProps) {
  const { t, i18n } = useTranslation()
  const { logout } = useAuth()

  // 非学生（教师等）端：逐字还原 github/dev 顶栏（语言简版 + 纯文本灰登出），不含学生重做的主题/規則/排行榜。
  if (legacy) {
    return (
      <>
        {/* 規則 + 排行榜（github/dev 原由 GamificationHeader 渲染）：绿色旧版样式，文字 ≥1180px 才显示 */}
        <RulesButton legacy />
        <GamificationLeaderboard legacy />

        {/* Inline controls — visible on ≥1180px：主题点 + 繁/EN + 文本登出 */}
        <div className={["hidden shrink-0 items-center justify-end gap-2 whitespace-nowrap min-[1180px]:flex", className]
          .filter(Boolean)
          .join(" ")}
        >
          <ThemeSelectorLegacy compact showLabel={false} />
          <LanguageBox legacy />
          <Button
            type="button"
            variant="ghost"
            onClick={logout}
            className="h-auto px-0 py-0 text-base font-normal text-gray-500 hover:bg-transparent hover:text-gray-700"
          >
            <Power className="mr-1" size={18} />
            {t("common.logout")}
          </Button>
        </div>

        {/* Compact controls — visible below 1180px：菜单折叠主题+语言，外露登出
            ZH-11/EN-02：Settings 触控区 32→44px；附当前语言缩写，让语言状态在折叠态可见 */}
        <div className="flex shrink-0 items-center gap-1 min-[1180px]:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 shrink-0 gap-1 px-1"
                aria-label={t('common.settings', { defaultValue: '設定' })}
                title={t('common.settings', { defaultValue: '設定' })}
              >
                <Settings className="h-5 w-5 text-muted-foreground" />
                <span className="text-[10px] font-semibold text-muted-foreground leading-none">
                  {(i18n.language || '').startsWith('en') ? 'EN' : (i18n.language || '').startsWith('zh-CN') ? '简' : '繁'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{t("common.themeLabel", { defaultValue: '主題色' })}</DropdownMenuLabel>
              <div className="px-2 py-1.5">
                <ThemeSelectorLegacy compact showLabel={false} />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("common.language")}</DropdownMenuLabel>
              <div className="px-2 py-1.5">
                <LanguageBox compact legacy />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={logout}
            aria-label={t("common.logout")}
            title={t("common.logout")}
            className="h-11 w-11 shrink-0 text-gray-500 hover:bg-transparent hover:text-gray-700"
          >
            <Power className="h-5 w-5" />
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Inline controls — 顺序按 Figma：主题选择 / 規則 / 语言 / 登出 */}
      <div
        className={["hidden shrink-0 items-center justify-end gap-2 whitespace-nowrap min-[1180px]:flex", className]
          .filter(Boolean)
          .join(" ")}
      >
        <ThemeSelector />
        <RulesButton />
        <GamificationLeaderboard />
        <LanguageBox />
        <Button
          type="button"
          variant="ghost"
          onClick={logout}
          className="h-auto rounded-full bg-accent px-4 py-2 text-sm font-medium text-[#1F2937] hover:brightness-95 hover:bg-accent"
        >
          <Power className="mr-1" size={16} />
          {t("common.logout")}
        </Button>
      </div>

      {/* Compact controls — <1180px：菜单折叠主题/语言，外露規則/登出
          ZH-11/EN-02：Settings 触控区 32→44px；附当前语言缩写，让语言状态在折叠态可见 */}
      <div className="flex shrink-0 items-center gap-1 min-[1180px]:hidden">
        <RulesButton compact />
        <GamificationLeaderboard compact />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 shrink-0 gap-1 px-1"
              aria-label={t('common.settings', { defaultValue: '設定' })}
              title={t('common.settings', { defaultValue: '設定' })}
            >
              <Settings className="h-5 w-5 text-muted-foreground" />
              <span className="text-[10px] font-semibold text-muted-foreground leading-none">
                {(i18n.language || '').startsWith('en') ? 'EN' : (i18n.language || '').startsWith('zh-CN') ? '简' : '繁'}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>{t("common.themeLabel", { defaultValue: '主題色' })}</DropdownMenuLabel>
            <div className="px-2 py-1.5">
              <ThemeSelector compact />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("common.language")}</DropdownMenuLabel>
            <div className="px-2 py-1.5">
              <LanguageBox compact />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={logout}
          aria-label={t("common.logout")}
          title={t("common.logout")}
          className="h-11 w-11 shrink-0 text-gray-500 hover:bg-transparent hover:text-gray-700"
        >
          <Power className="h-5 w-5" />
        </Button>
      </div>
    </>
  )
}
